import { type ReactNode } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useSuspenseQuery } from '@tanstack/react-query';

import { Page } from '@/components/layout/Page';
import { Section } from '@/components/layout/Section';
import { PageHeader } from '@/components/composed/PageHeader';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { TpaDetail, tpaDetailQuery, tpaSections } from '@/features/tpas';
import { EntityUsersSection, entityUsersQuery } from '@/features/users';
import {
  AdminNotificationPreferences,
  NotificationPreferenceHistory,
} from '@/features/portal';
import { usePageMeta } from '@/app/shell/use-page-meta';
import { useRailSubNav } from '@/app/shell/use-rail-sub-nav';
import { resolveSection } from '@/lib/entity-sections';
import { tpaId } from '@/lib/api/ids';

export const Route = createFileRoute('/_auth/tpas/$tpaId/$section')({
  loader: ({ context, params }) =>
    context.queryClient.ensureQueryData(tpaDetailQuery(tpaId(params.tpaId))),
  component: TpaDetailPage,
});

/*
 * Injects a typed router <Link> into the domain-free RailSubNav. The rail sub-nav
 * builds the full `/tpas/{id}/{section}` href; we pass it straight through to
 * `to` so every param survives — client navigation and deep-linking both work.
 */
function renderLink(props: {
  href: string;
  children: ReactNode;
  className?: string;
  'aria-current'?: 'page';
  'data-section'?: string;
}) {
  const { href, children, ...rest } = props;
  return (
    <Link to={href} {...rest}>
      {children}
    </Link>
  );
}

/*
 * The administrator on-behalf notification surfaces for a user of this
 * organization (F02 R27/R28/R29), composed at the route so the tpas feature
 * imports neither users nor portal. A target user is chosen from the entity's
 * own user list (the scoped read the Users section already uses); the chosen
 * account's four choices (AdminNotificationPreferences) and its change history
 * (NotificationPreferenceHistory) are shown. The target is addressed by id —
 * the name is shown in the picker, never sent (AGENTS.md: ids only).
 */
function TpaUserNotifications({ entityId }: { entityId: string }) {
  const { data: users } = useQuery(entityUsersQuery(entityId));
  const options = users ?? [];
  const [targetId, setTargetId] = useState<string>('');
  const selected = targetId || options[0]?.id || '';
  const selectedUser = options.find((u) => u.id === selected);

  if (options.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No users at this organization yet.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-1">
        <label htmlFor="notif-target-user" className="text-sm font-medium">
          User
        </label>
        <select
          id="notif-target-user"
          className="w-fit rounded-md border border-input bg-background px-3 py-2 text-sm"
          value={selected}
          onChange={(e) => setTargetId(e.target.value)}
        >
          {options.map((u) => (
            <option key={u.id} value={u.id}>
              {u.displayName}
            </option>
          ))}
        </select>
      </div>
      {selectedUser ? (
        <>
          {/* `selectedUser.id` is already the branded UserId from the users
              read — threaded through, not re-branded (ADR 0007). */}
          <AdminNotificationPreferences
            targetUserId={selectedUser.id}
            targetUserName={selectedUser.displayName}
          />
          <NotificationPreferenceHistory userId={selectedUser.id} />
        </>
      ) : null}
    </div>
  );
}

function TpaDetailPage() {
  const { tpaId: tpaIdParam, section } = Route.useParams();
  const id = tpaId(tpaIdParam);
  const { data: tpa } = useSuspenseQuery(tpaDetailQuery(id));
  const activeSectionId =
    resolveSection(section, tpaSections) ?? tpaSections[0]!.id;

  usePageMeta({ title: 'TPA' });
  // Publish the entity sub-nav into the shell rail. Domain data
  // (sections, active id, name) originates here and flows to domain-free chrome.
  useRailSubNav({
    sections: tpaSections,
    activeSectionId,
    basePath: `/tpas/${id}`,
    entityName: tpa.name,
    backHref: '/tpas',
    backLabel: 'All TPAs',
    navLabel: 'TPA sections',
    renderLink,
  });

  return (
    <Page>
      <PageHeader title="TPA" />
      <Section>
        {/* The Users section is composed HERE at the route (routes → features)
            so the tpas feature need not import the users feature (AC-5f7370f6).
            The entity id is a plain string at this boundary — the TPA user
            routes are untyped shims not in openapi.json (ADR 0009), so there is
            no EntityId brand to thread; pass String(id) at this one boundary. */}
        {activeSectionId === 'users' ? (
          <EntityUsersSection entityId={String(id)} orgKind="tpa" />
        ) : activeSectionId === 'notifications' ? (
          <TpaUserNotifications entityId={String(id)} />
        ) : (
          <TpaDetail tpaId={id} section={section} />
        )}
      </Section>
    </Page>
  );
}
